<template>
  <nav>
    <v-dialog
      v-if="!loadingData"
      v-model="dialog"
      data-testid="hub-edit-dialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      persistent
    >
      <v-card data-testid="hub-edit-card" class="hub-edit-card">
        <v-form
          ref="form"
          v-model="valid"
          @keyup.enter="updateHub"
          @submit.prevent
        >
          <v-toolbar
            dark
            color="accent"
          >
            <v-btn
              data-testid="close-button"
              icon
              dark
              @click="onDialogClose"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title class="text-h5 font-weight-bold">
              {{ $t('editHubTitle') }}
            </v-toolbar-title>
            <v-spacer />
            <v-btn
              data-testid="submit-button"
              type="submit"
              :disabled="loading"
              color="white"
              class="text-body-1 darktext--text text-none rounded-xxl px-4"
              @click="updateHub"
            >
              {{ $t('save') }}
            </v-btn>
          </v-toolbar>
          <AlertMessages
            v-if="alertValue"
            :alert-type="alertType" 
            :alert-msg="alertMsg"
            @reset="alertValue = false"
          />
          <v-card-text class="pt-0 pl-6">
            <div class="py-15 px-md-12">
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <p class="font-weight-bold text-h6 darktext--text">
                    {{ $t('labelTitle') }}
                  </p>
                  <v-text-field
                    v-model="name"
                    data-testid="hub-title-input"
                    solo
                    hide-details="auto"
                    elevation="6"
                    class="rounded-xxl text-body-2 mb-5"
                    :rules="hubTitleRules"
                  />
                  <p class="font-weight-bold text-h6 darktext--text">
                    {{ $t('labelDescription') }}
                  </p>
                  <v-textarea
                    v-model="descriptionHub"
                    data-testid="hub-description"
                    solo
                    hide-details="auto"
                    class="rounded-xxl text-body-2"
                    :placeholder="$t('labelDescriptionHub')"
                    :rules="descriptionRules"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <p class="font-weight-bold text-h6 darktext--text">
                    {{ $t('labelCoverImage') }}
                  </p>
                  <v-hover>
                    <template v-slot:default="{ hover }">
                      <v-card
                        align="center"
                        data-testid="hub-cover-card"
                        rounded="md"
                        class="pa-0 rounded-xxl"
                        :elevation="hover ? 8 : 5"
                        max-width="600"
                      >
                        <v-card-actions class="pa-0 ">
                          <v-img
                            class="rounded-xxl"
                            max-height="280"
                            :src="cover != null ? cover : coverURL"
                          />
                          <v-file-input 
                            ref="file_input_cover"
                            v-model="coverImageData"
                            accept="image/png, image/jpeg"
                            data-testid="cover-file-input"
                            class="d-none"
                            @change="uploadCover"
                          />
                          <div class="center-button-on-image">
                            <v-btn
                              :loading="loadingCover"
                              data-testid="upload-cover-button"
                              mx-auto
                              color="accent"
                              class="rounded-xxl text-none px-10"
                              elevation="2"
                              relative
                              bottom
                              left
                              @click="showUploadCover"
                            >
                              {{ $t('upload') }}
                            </v-btn>
                            <v-btn 
                              data-testid="upload-unsplash-cover-button"
                              mx-auto color="accent"
                              class="rounded-xxl text-none px-10 d-block mx-auto mt-3"
                              :class="{'disable-events': loadingCover}"
                              elevation="2"
                              relative
                              bottom
                              left
                              @click="toggleUnsplash">
                            {{ $t('browse') }}
                          </v-btn>
                          </div>
                        </v-card-actions>
                      </v-card>
                    </template>
                  </v-hover>
                </v-col>
              </v-row>

              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  class="checkbox-align-start"
                >
                  <p class="font-weight-bold text-h6 darktext--text mt-5">
                    {{ $t('tagsLabel') }}
                  </p>
                  <v-autocomplete
                    v-model="tags"
                    :items="tagSuggestions"
                    multiple
                    chips
                    clearable
                    outlined
                    small-chips
                    solo
                    hide-selected
                    rounded
                    no-filter
                    return-object
                    data-testid="tags-input"
                    :search-input.sync="search"
                    @blur="addNewTag"
                    @change="onTagSelection"
                    @keydown.enter.prevent="addNewTag"
                  >
                    <template v-slot:selection="{ item }">
                      <v-chip
                        close
                        @click:close="removeTag(item)"
                        color="accent"
                      >
                        {{ item }}
                      </v-chip>
                    </template>
                    <template v-slot:prepend-inner>
                      <v-progress-circular
                        v-if="isLoading"
                        size="20"
                        indeterminate
                      />
                    </template>
                  </v-autocomplete>
                  
              </v-col>
              </v-row>

              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  class="radio-align-start"
                >
                  <p
                    data-testid="sharing-setting-label"
                    class="font-weight-bold text-h6 darktext--text mt-5"
                  >
                    {{ $t('SharingSettingsLabel') }}
                  </p>
                  <v-radio-group v-model="sharingSettings">
                    <v-radio
                      key="1"
                      :label="$t('hubSharingSettingsOption1')"
                      value="1"
                      color="accent"
                      class="black--text"
                      data-testid="sharing-setting-options"
                      @change="changeSharingOption(1)"
                    />
                    <v-tooltip bottom :disabled="!autoShareWithActivities">
                      <template v-slot:activator="{ on }">
                        <div v-on="on">
                          <v-radio
                            key="3"
                            :label="$t('hubSharingSettingsOption3')"
                            value="3"
                            color="accent"
                            class="black--text"
                            data-testid="sharing-setting-options"
                            @change="changeSharingOption(3)"
                            :disabled="autoShareWithActivities"
                          />
                        </div>
                      </template>
                      <span>{{ $t('autoShareHubPreventChangeSharingSettingTooltip') }}</span>
                    </v-tooltip>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  class="checkbox-align-start"
                >
                  <p
                    data-testid="auto-share-label"
                    class="font-weight-bold text-h6 darktext--text mt-5"
                  >
                    {{ $t('autoSharingLabel') }}
                  </p>
                  <v-checkbox
                    v-model="autoShare"
                    color="accent"
                    :class="{ 'selected' : autoShare}"
                    data-testid="auto-share-checkbox"
                    hide-details
                    @change="autoShareHub"
                  >
                    <template v-slot:label>
                      <span class="text-subtitle-1">{{ $t('autoSharingText') }}</span><br> 
                    </template>
                  </v-checkbox>
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-form>
      </v-card>
    </v-dialog>
    <ConfirmationDialog 
      v-if="dialogVisibleChangeSharingSetting"
      ref="confirmationDialogChangeSharingSetting"
      :visible="dialogVisibleChangeSharingSetting" 
      :cd-title="confirmationDialogTitle" 
      :cd-text="confirmationDialogText" 
      :cd-submit-btn="confirmationDialogSubmitBtn" 
      :cd-cancel-btn="confirmationDialogCancelBtn" 
      data-testid="confirmation-dialog-change-sharing-setting" 
      cd-submit-btn-color="accent"
      @close="resetSharingSetting"
      @proceedSubmit="dialogVisibleChangeSharingSetting=false"
    />
    <ConfirmationDialog 
      v-if="dialogVisibleChangeAutoShare"
      ref="confirmationDialogChangeAutoShare"
      :visible="dialogVisibleChangeAutoShare"
      :cd-title="confirmationDialogTitle" 
      :cd-text="confirmationDialogText" 
      :cd-submit-btn="confirmationDialogSubmitBtn" 
      :cd-cancel-btn="confirmationDialogCancelBtn" 
      data-testid="confirmation-dialog-change-auto-share" 
      cd-submit-btn-color="accent"
      @close="resetAutoShare"
      @proceedSubmit="dialogVisibleChangeAutoShare=false" 
    />
    <UnsplashSearch 
      v-if="showUnsplash"
      ref="unsplashDialog"
      :visible="showUnsplash"
      data-testid="unsplash-dialog"
      @close="showUnsplash = false"
      @applyPhoto="embedUnsplash"
    />
  </nav>
</template>
<script>
import i18n from '../../plugins/i18n'
import { uploadCoverImage, updateHub, getHubSharingSetings } from '../../api/hubApi'
import { getTags } from '../../api/activityApi'
import { ruleRequired, ruleValidMin2Max70Characters, ruleValidMin2Characters } from '../../rules/formRules.js';
import AlertMessages  from '../../components/AlertMessages'
import ConfirmationDialog from '../../components/ConfirmationDialog.vue'
import UnsplashSearch from '@/components/unsplash/UnsplashSearch.vue';

export default {
    components: {
        AlertMessages,
        ConfirmationDialog,
        UnsplashSearch
    },
    props: {
        visible: Boolean,
        hubId: {
          type: String,
          default: ''
        },
        hubTitle: {
          type: String,
          default: ''
        },
        hubDescription: {
          type: String,
          default: ''
        },
        hubImageURL: {
          type: String,
          default: ''
        },
        hubImagePath: {
          type: String,
          default: ''
        },
        hubSharingSettings: {
          type: String,
          default: ''
        },
        hubAutoShare: {
          type: Boolean,
          default: false
        },
        autoShareWithActivities: {
          type: Boolean,
          default: false
        },
        hubTags: {
          type: Array,
          default: () => [],
        }
    },
    data(){
        return{
            valid: true,
            alertMsg: '',
            alertValue : false,
            spaceId: window.$cookies.get('space_id'),
            hubTitleRules: ruleRequired('labelTitle').concat(ruleValidMin2Max70Characters('labelTitle')),
            descriptionRules:ruleValidMin2Characters('labelDescription'),
            val: '',
            valDescription:'',
            valCover: '',
            valCoverPath: '',
            valSharingSettings: '',
            coverImageData: null,
            coverURL: require('../../assets/background-hub.jpg'),
            loadingCover: false,
            buttonDisabled: false,
            sharingOptions: [],
            dialogVisibleChangeSharingSetting: false,
            confirmationDialogTitle: '',
            confirmationDialogText: '',
            confirmationDialogCancelBtn: i18n.t('cancel'),
            confirmationDialogSubmitBtn: i18n.t('ok'),
            loadingData: false,
            valAutoShare: false,
            dialogVisibleChangeAutoShare: false,
            showUnsplash: false,
            valTags: [],
            tagSuggestions: [],
            isLoading: false,
            search: ''           
         }
    },
    computed: {
        dialog: {
            get () {
                return this.visible
            },
            set (value) {
                if (!value) {
                    this.$emit('close')
                }
         
            }
        },

        name:
        {
            get()
            {
                return this.val
            },
            set(hubTitle)
            {
                this.val = hubTitle
            }
        },
        descriptionHub:
        {
          get()
            {
                return this.valDescription
            },
            set(hubDescription)
            {
                this.valDescription = hubDescription
            }
        },
        cover:
        {
            get()
            {
                return this.valCover
            },
            set(hubImageURL)
            {
                this.valCover = hubImageURL
            }
        },
        coverPath:
        {
            get()
            {
                return this.valCoverPath
            },
            set(hubImagePath)
            {
                this.valCoverPath = hubImagePath
            }
        },
        sharingSettings:
        {
            get()
            {
                return this.valSharingSettings
            },
            set(hubSharingSettings)
            {
                this.valSharingSettings = hubSharingSettings
            }
        },
        autoShare:
        {
            get()
            {
                return this.valAutoShare
            },
            set(hubAutoShare)
            {
                this.valAutoShare = hubAutoShare
            }
        },
        tags:{
          get()
          {
              return this.valTags
          },
          set(hubTags)
          {
              this.valTags = hubTags
          }
        },
        loading(){
            if(this.loadingCover || this.buttonDisabled){
                return true
            }
            return false
        }
    },
    watch: {
        hubTitle(newVal) {
            this.val = newVal
        },
        hubDescription(newValDescription){
          this.valDescription = newValDescription
        },
        hubImageURL(newValCover){
            this.valCover = newValCover
        },
        hubImagePath(newValCoverPath){
            this.valCoverPath = newValCoverPath
        },
        hubSharingSettings(newValsharing) {
            this.valSharingSettings = newValsharing
        },
        hubAutoShare(newValAutoShare) {
            this.valAutoShare = newValAutoShare
        },
        hubTags(tags) {
          this.valTags = tags
        },
        async search(val) {
          this.fetchTags(val);
        },
    },
    created() {
        this.loadingData = true
        this.fetchSharingSettings()
        this.name = this.hubTitle
        this.descriptionHub = this.hubDescription
        this.cover = this.hubImageURL
        this.coverPath = this.hubImagePath
        this.sharingSettings = this.hubSharingSettings
        this.autoShare = this.hubAutoShare
        this.alertMsg = ''
        this.loadingData = false
        this.tags = this.hubTags
        this.tagSuggestions = this.hubTags;
    },
    methods: {
        async updateHub(){
            this.$refs.form.validate()
            if(this.valid){
                this.buttonDisabled = true
                var alertType = ''
                var alertMsg = ''
                var attributes = {}

                if(this.coverPath == null){
                    attributes = {
                                title: this.name.trim(),
                                description: this.descriptionHub,
                                sync_with_space_members: this.autoShare,
                                tags: this.tags
                            }
                }else{
                    attributes= {
                                title: this.name.trim(),
                                description: this.descriptionHub,
                                image: this.coverPath,
                                sync_with_space_members: this.autoShare,
                                tags: this.tags
                            }
                }
                try {
                    let data = {
                        data: {
                            type: "hubs",
                            id: this.hubId, 
                            attributes: attributes,
                            relationships: {
                                space: {
                                    data: {
                                        type: "spaces",
                                        id: this.spaceId
                                    }
                                },
                                "sharing-setting": {
                                    data: {
                                        type: "sharing-settings",
                                        id: this.sharingSettings
                                    }
                                }
                            },
                        }
                    };

                    var response = await updateHub(this.hubId, data);
                    if(response.status == 200) {
                        alertMsg =  i18n.t('editHubSuccess')
                        alertType = 'success'
                        this.$emit('proceedSubmit', alertMsg, alertType, this.name, this.descriptionHub, this.cover, this.coverPath, this.sharingSettings, this.autoShare, this.tags)
                        this.$emit('close')                     }
                } catch (e) {
                    if(e.response){
                        if (e.response.status == 403){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].detail == 'This action is unauthorized.'){
                                    alertType = 'error'
                                    alertMsg =  i18n.t('forbidden')
                                    this.$emit('proceedSubmit', alertMsg, alertType)
                                    this.$emit('close')  
                                }
                                else {
                                    alertType = 'error'
                                    alertMsg =  i18n.t('serverError')
                                    this.$emit('proceedSubmit', alertMsg, alertType)
                                    this.$emit('close')  
                                }
                            }
                        } else{
                            alertType = 'error'
                            alertMsg =  i18n.t('serverError')
                            this.$emit('proceedSubmit', alertMsg, alertType)
                            this.$emit('close') 
                        }     
                    }
                }
                this.buttonDisabled = false
            }
        },
        onDialogClose(){
            this.$emit('close')
        },
        async uploadCover(){
            if(this.coverImageData.size > 5*1024*1024){
                this.alertType = 'error';
                this.alertMsg =  i18n.t('uploadCoverError')
                this.alertValue = true
            }else {
                this.loadingCover = true
                try {
                    let data = new FormData();
                    data.append('file', this.coverImageData);
                    data.append('type', 'cover');
                    data.append('space_id', this.spaceId);

                    const response = await uploadCoverImage(data) 
                    if(response.status == 200) {
                        this.loadingCover = false
                        this.alertMsg = ''
                        this.cover = URL.createObjectURL(this.coverImageData)
                        this.coverPath = response.data.meta.detail
                    }
                } catch (e) {
                    if(e.response){
                        this.alertType = 'error'
                        this.alertValue = true
                        if (e.response.status == 422 ){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].meta[0].code == 'extensionUnsupported' || e.response.data.errors[i].meta[0].code == 'fileOversize'){
                                    this.alertMsg =  i18n.t('uploadCoverError')
                                }else {
                                    this.alertMsg = i18n.t('serverError')
                                }
                            }
                        } else if (e.response.status == 403){
                             for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].detail == 'Storage is full'){
                                    this.alertMsg =  i18n.t('storageFullError')
                                }
                                else {
                                    this.$router.push({name: 'PageAccessDenied'})
                                }
                            }
                        } else {
                            this.alertMsg =  i18n.t('serverError')       
                        }                     
                    }
                    this.loadingCover = false
                }
            }
        },
        showUploadCover(){
            this.$refs.file_input_cover.$refs.input.click()
        },
        async fetchSharingSettings() {
            try {
                const response = await getHubSharingSetings()
                if(response){
                    if(response.status == 200) {
                        this.sharingOptions = response.data.data
                    }
                }            
            } catch (e) {
                if(e.response){
                    this.alertValue = true
                    this.alertType = 'error';
                    this.alertMsg =  i18n.t('serverError');
                } 
            }
        },
        changeSharingOption(newVal){
          if(newVal == 1 && this.hubSharingSettings != 1)
          {
              this.confirmationDialogTitle= i18n.t('confirmationDialogTitleChangeHubSharingSetting')
              this.confirmationDialogText=  i18n.t('confirmationDialogTextChangeHubSharingSetting')
              this.dialogVisibleChangeSharingSetting = true
          }
        },
        resetSharingSetting(){
            this.sharingSettings = this.hubSharingSettings
            this.dialogVisibleChangeSharingSetting = false
        },
        autoShareHub(newVal){
          if(newVal == true)
          {
              this.confirmationDialogTitle= i18n.t('confirmationDialogTitleChangeHubAutoShare')
              this.confirmationDialogText=  i18n.t('confirmationDialogTextChangeHubAutoShare')
              this.dialogVisibleChangeAutoShare = true
          }
        },
        resetAutoShare(){
            this.autoShare = this.hubAutoShare
            this.dialogVisibleChangeAutoShare = false
        },
        toggleUnsplash(){
          this.showUnsplash = !this.showUnsplash
        },

        embedUnsplash(photo){
          this.showUnsplash = false
          this.cover = photo.urls.regular
          this.coverPath = photo.urls.regular
        },
        onTagSelection() {
          this.search = '';
        },
        addNewTag() {
          const tagExists = this.tagSuggestions.includes(this.search);
          if (this.search && !tagExists && !this.tags.includes(this.search)) {
            this.tags.push(this.search); // Add new tag
            this.tagSuggestions.push(this.search); // Add new tag
          }
          this.search = ''; // Clear the input field
        },
        removeTag(tag) {
          this.tags = this.tags.filter((t) => t !== tag);
        },
        async fetchTags(val) {

          if (this.isLoading) return;

          if (!val || val.trim().length < 2) {
            this.tagSuggestions = Array.from(new Set([...this.hubTags, ...this.tags]));
            return;
          }

          this.isLoading = true;

          try {
            const response = await getTags(this.spaceId, val);
            if (response?.status === 200) {
              const entries = response.data.data.map((tag) => tag.attributes.name);
              this.tagSuggestions = Array.from(
                new Set([...entries, ...this.hubTags, ...this.tags])
              );
            }
          } catch (e) {
            if (e.response) {
              this.alertValue = true;
              this.alertType = "error";
              this.alertMsg = i18n.t("serverError");
            }
          } finally {
            this.isLoading = false;
          }
        },

    }
}
</script>